import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {
  BarChart3,
  Bell,
  Box,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Edit3,
  FileBarChart2,
  Folder,
  GripVertical,
  Layers3,
  LayoutDashboard,
  Loader2,
  MapPin,
  MoreHorizontal,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  PieChart,
  Plus,
  Search,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Table2,
  Trash2,
  TrendingUp,
  Users,
  X,
} from 'lucide-react'
import * as API from '../../api/index'
import {isSuperAdmin} from '../../modules/auth/core/Auth'
import {MenuEditor, MenuModalContent} from './GeneralMenu.styles'

type MenuItemType = 'category' | 'product' | 'subproduct'

type MenuItem = {
  guid: string
  name: string
  originalName?: string
  type: MenuItemType
  icon: string
  sortOrder: number
  isOpen?: boolean
  children: MenuItem[]
}

type EditorState = {
  mode: 'create' | 'edit'
  type: MenuItemType
  item?: MenuItem
  parent?: MenuItem
}

type DragState = {
  guid: string
  type: MenuItemType
  parentGuid: string | null
}

type Feedback = {kind: 'success' | 'error'; message: string} | null

const iconOptions = [
  {key: 'bi-grid', label: 'Visão geral', Icon: LayoutDashboard},
  {key: 'bi-bar-chart', label: 'Gráfico', Icon: BarChart3},
  {key: 'bi-pie-chart', label: 'Análises', Icon: PieChart},
  {key: 'bi-graph-up', label: 'Desempenho', Icon: TrendingUp},
  {key: 'bi-people', label: 'Pessoas', Icon: Users},
  {key: 'bi-building', label: 'Empresas', Icon: Building2},
  {key: 'bi-cart', label: 'Carrinho', Icon: ShoppingCart},
  {key: 'bi-bag', label: 'Negócios', Icon: ShoppingBag},
  {key: 'bi-box', label: 'Produto', Icon: Package},
  {key: 'bi-file-earmark-text', label: 'Relatório', Icon: FileBarChart2},
  {key: 'bi-table', label: 'Tabela', Icon: Table2},
  {key: 'bi-bell', label: 'Alertas', Icon: Bell},
  {key: 'bi-geo-alt', label: 'Localização', Icon: MapPin},
  {key: 'bi-currency-dollar', label: 'Financeiro', Icon: CircleDollarSign},
  {key: 'bi-briefcase', label: 'Trabalho', Icon: BriefcaseBusiness},
  {key: 'bi-layers', label: 'Camadas', Icon: Layers3},
  {key: 'bi-lightning', label: 'Destaque', Icon: Sparkles},
  {key: 'bi-calendar', label: 'Calendário', Icon: CalendarDays},
] as const

const itemLabels: Record<MenuItemType, {singular: string; plural: string}> = {
  category: {singular: 'categoria', plural: 'categorias'},
  product: {singular: 'produto', plural: 'produtos'},
  subproduct: {singular: 'subproduto', plural: 'subprodutos'},
}

const defaultIcons: Record<MenuItemType, string> = {
  category: 'bi-grid',
  product: 'bi-box',
  subproduct: 'bi-file-earmark-text',
}

const getIcon = (iconKey: string) =>
  iconOptions.find((option) => option.key === iconKey)?.Icon || MoreHorizontal

const findItem = (items: MenuItem[], guid: string): MenuItem | undefined => {
  for (const item of items) {
    if (item.guid === guid) return item
    const child = findItem(item.children, guid)
    if (child) return child
  }
  return undefined
}

const reorderSiblings = (
  items: MenuItem[],
  parentGuid: string | null,
  sourceGuid: string,
  targetGuid: string
): MenuItem[] => {
  const reorder = (siblings: MenuItem[]) => {
    const from = siblings.findIndex((item) => item.guid === sourceGuid)
    const to = siblings.findIndex((item) => item.guid === targetGuid)
    if (from < 0 || to < 0 || from === to) return siblings
    const next = [...siblings]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    return next.map((item, index) => ({...item, sortOrder: index + 1}))
  }

  if (!parentGuid) return reorder(items)
  return items.map((item) =>
    item.guid === parentGuid
      ? {...item, children: reorder(item.children)}
      : {...item, children: reorderSiblings(item.children, parentGuid, sourceGuid, targetGuid)}
  )
}

const filterMenu = (items: MenuItem[], query: string): MenuItem[] => {
  const normalized = query.trim().toLocaleLowerCase('pt-BR')
  if (!normalized) return items
  return items.reduce<MenuItem[]>((result, item) => {
    const children = filterMenu(item.children, normalized)
    if (item.name.toLocaleLowerCase('pt-BR').includes(normalized) || children.length) {
      result.push({...item, isOpen: true, children})
    }
    return result
  }, [])
}

const ItemEditorModal: FC<{
  state: EditorState | null
  saving: boolean
  onClose: () => void
  onSave: (name: string, icon: string) => void
}> = ({state, saving, onClose, onSave}) => {
  const [name, setName] = useState('')
  const [icon, setIcon] = useState(defaultIcons.category)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!state) return
    setName(state.item?.name || '')
    setIcon(state.item?.icon || defaultIcons[state.type])
    window.setTimeout(() => inputRef.current?.focus(), 100)
  }, [state])

  if (!state) return null
  const label = itemLabels[state.type].singular

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const trimmedName = name.trim()
    if (trimmedName && !saving) onSave(trimmedName, icon)
  }

  return (
    <Modal show centered onHide={onClose} dialogClassName='mw-650px'>
      <MenuModalContent>
        <form onSubmit={submit}>
          <div className='gm-modal-header'>
            <div>
              <div className='gm-modal-eyebrow'>
                {state.mode === 'create' ? 'Novo item' : 'Editar item'}
              </div>
              <h2 className='gm-modal-title'>
                {state.mode === 'create' ? `Criar ${label}` : `Editar ${label}`}
              </h2>
              <p className='gm-modal-subtitle'>
                {state.parent
                  ? `Será adicionado dentro de “${state.parent.name}”.`
                  : 'Este item aparecerá no primeiro nível do menu.'}
              </p>
            </div>
            <button type='button' className='gm-modal-close' onClick={onClose} aria-label='Fechar'>
              <X size={18} />
            </button>
          </div>
          <div className='gm-modal-body'>
            <label className='gm-field-label' htmlFor='menu-item-name'>
              Nome do {label}
            </label>
            <input
              ref={inputRef}
              id='menu-item-name'
              className='gm-name-input'
              maxLength={120}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={`Ex.: ${state.type === 'category' ? 'Inteligência comercial' : 'Visão de vendas'}`}
            />
            <div className='gm-field-label gm-field-label--icon'>
              Ícone <span className='gm-field-hint'>Ele acompanha o item ao reordenar</span>
            </div>
            <div className='gm-icon-grid' role='radiogroup' aria-label='Escolha um ícone'>
              {iconOptions.map(({key, label: iconLabel, Icon}) => (
                <button
                  type='button'
                  key={key}
                  className={`gm-icon-option ${icon === key ? 'gm-icon-option--selected' : ''}`}
                  onClick={() => setIcon(key)}
                  role='radio'
                  aria-checked={icon === key}
                  title={iconLabel}
                >
                  <Icon size={20} />
                  {icon === key && <span className='gm-icon-check'><Check size={9} strokeWidth={3} /></span>}
                </button>
              ))}
            </div>
          </div>
          <div className='gm-modal-footer'>
            <button type='button' className='gm-modal-button gm-modal-button--secondary' onClick={onClose}>
              Cancelar
            </button>
            <button
              type='submit'
              className='gm-modal-button gm-modal-button--primary'
              disabled={!name.trim() || saving}
            >
              {saving && <Loader2 className='gm-spinner' size={15} />}
              {state.mode === 'create' ? 'Criar item' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      </MenuModalContent>
    </Modal>
  )
}

const DeleteModal: FC<{
  item: MenuItem | null
  deleting: boolean
  onClose: () => void
  onConfirm: () => void
}> = ({item, deleting, onClose, onConfirm}) => {
  if (!item) return null
  const descendants = item.children.reduce((total, child) => total + 1 + child.children.length, 0)

  return (
    <Modal show centered onHide={onClose} dialogClassName='mw-500px'>
      <MenuModalContent>
        <div className='gm-modal-header'>
          <div>
            <div className='gm-modal-eyebrow'>Confirmar exclusão</div>
            <h2 className='gm-modal-title'>Excluir “{item.name}”?</h2>
          </div>
          <button type='button' className='gm-modal-close' onClick={onClose} aria-label='Fechar'>
            <X size={18} />
          </button>
        </div>
        <div className='gm-modal-body'>
          <div className='gm-delete-note'>
            <strong>Esta ação não pode ser desfeita.</strong>
            {descendants
              ? `Este item contém ${descendants} ${descendants === 1 ? 'item vinculado' : 'itens vinculados'}. Eles deixarão de aparecer no menu.`
              : 'O item deixará de aparecer para os usuários que possuem acesso a ele.'}
          </div>
        </div>
        <div className='gm-modal-footer'>
          <button type='button' className='gm-modal-button gm-modal-button--secondary' onClick={onClose}>
            Cancelar
          </button>
          <button
            type='button'
            className='gm-modal-button gm-modal-button--danger'
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting && <Loader2 className='gm-spinner' size={15} />}
            Excluir item
          </button>
        </div>
      </MenuModalContent>
    </Modal>
  )
}

const GeneralMenu: FC = () => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [query, setQuery] = useState('')
  const [feedback, setFeedback] = useState<Feedback>(null)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [deleteItem, setDeleteItem] = useState<MenuItem | null>(null)
  const [dragging, setDragging] = useState<DragState | null>(null)
  const [dragOverGuid, setDragOverGuid] = useState<string | null>(null)
  const orderSavingRef = useRef(false)
  const canEdit = !!isSuperAdmin()

  const loadData = async (preserveOpenState = true) => {
    try {
      const response = await API.loadGeneralMenu()
      if (!response?.success) throw new Error(response?.error || 'Não foi possível carregar o menu.')
      setMenuItems((current) => {
        const openItems = new Set<string>()
        if (preserveOpenState) {
          current.forEach((category) => {
            if (category.isOpen) openItems.add(category.guid)
            category.children.forEach((product) => product.isOpen && openItems.add(product.guid))
          })
        }
        return (response.data || []).map((category: any, categoryIndex: number) => ({
          guid: category.guid,
          name: category.name,
          originalName: category.admin_name || undefined,
          type: 'category' as const,
          icon: category.icon || defaultIcons.category,
          sortOrder: Number(category.sort_order || categoryIndex + 1),
          isOpen: preserveOpenState && current.length ? openItems.has(category.guid) : true,
          children: (category.products || []).map((product: any, productIndex: number) => ({
            guid: product.guid,
            name: product.name,
            originalName: product.admin_name || undefined,
            type: 'product' as const,
            icon: product.icon || defaultIcons.product,
            sortOrder: Number(product.sort_order || productIndex + 1),
            isOpen: preserveOpenState && current.length ? openItems.has(product.guid) : true,
            children: (product.subproducts || []).map((subproduct: any, subproductIndex: number) => ({
              guid: subproduct.guid,
              name: subproduct.name,
              originalName: subproduct.admin_name || undefined,
              type: 'subproduct' as const,
              icon: subproduct.icon || defaultIcons.subproduct,
              sortOrder: Number(subproduct.sort_order || subproductIndex + 1),
              children: [],
            })),
          })),
        }))
      })
    } catch (error: any) {
      setFeedback({kind: 'error', message: error?.message || 'Não foi possível carregar o menu.'})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData(false)
  }, [])

  useEffect(() => {
    if (!feedback) return
    const timer = window.setTimeout(() => setFeedback(null), 4200)
    return () => window.clearTimeout(timer)
  }, [feedback])

  const totals = useMemo(() => {
    const products = menuItems.reduce((total, category) => total + category.children.length, 0)
    const subproducts = menuItems.reduce(
      (total, category) =>
        total + category.children.reduce((sum, product) => sum + product.children.length, 0),
      0
    )
    return {categories: menuItems.length, products, subproducts}
  }, [menuItems])

  const visibleItems = useMemo(() => filterMenu(menuItems, query), [menuItems, query])

  const toggleItem = (guid: string) => {
    const toggle = (items: MenuItem[]): MenuItem[] =>
      items.map((item) =>
        item.guid === guid
          ? {...item, isOpen: !item.isOpen}
          : {...item, children: toggle(item.children)}
      )
    setMenuItems(toggle)
  }

  const setAllOpen = (isOpen: boolean) => {
    const update = (items: MenuItem[]): MenuItem[] =>
      items.map((item) => ({
        ...item,
        isOpen: item.children.length ? isOpen : item.isOpen,
        children: update(item.children),
      }))
    setMenuItems(update)
  }

  const persistSiblingOrder = async (items: MenuItem[], parentGuid: string | null) => {
    const siblings = parentGuid ? findItem(items, parentGuid)?.children || [] : items
    const response = await API.saveMenuOrder(
      siblings.map((item, index) => ({guid: item.guid, type: item.type, sort_order: index + 1}))
    )
    if (!response?.success) throw new Error(response?.error || 'Não foi possível salvar a ordem.')
  }

  const moveItem = async (source: DragState, targetGuid: string, announce = true) => {
    if (source.guid === targetGuid || orderSavingRef.current) return
    orderSavingRef.current = true
    const previous = menuItems
    const next = reorderSiblings(previous, source.parentGuid, source.guid, targetGuid)
    setMenuItems(next)
    try {
      await persistSiblingOrder(next, source.parentGuid)
      window.dispatchEvent(new Event('menu:updated'))
      if (announce) setFeedback({kind: 'success', message: 'Nova ordem salva automaticamente.'})
    } catch (error: any) {
      setMenuItems(previous)
      setFeedback({kind: 'error', message: error?.message || 'Não foi possível salvar a nova ordem.'})
    } finally {
      orderSavingRef.current = false
    }
  }

  const moveByOffset = (item: MenuItem, parentGuid: string | null, offset: -1 | 1) => {
    const siblings = parentGuid ? findItem(menuItems, parentGuid)?.children || [] : menuItems
    const index = siblings.findIndex((sibling) => sibling.guid === item.guid)
    const target = siblings[index + offset]
    if (target) moveItem({guid: item.guid, type: item.type, parentGuid}, target.guid)
  }

  const saveEditor = async (name: string, icon: string) => {
    if (!editor) return
    setSaving(true)
    try {
      let response: any
      if (editor.mode === 'edit' && editor.item) {
        response = await API.updateMenuItem(editor.type, editor.item.guid, name, icon)
      } else if (editor.type === 'category') {
        response = await API.createCategory(name, icon)
      } else if (editor.type === 'product' && editor.parent) {
        response = await API.createProduct(name, editor.parent.guid, icon)
      } else if (editor.type === 'subproduct' && editor.parent) {
        response = await API.createSubProduct(name, editor.parent.guid, icon)
      }
      if (!response?.success) throw new Error(response?.error || 'Não foi possível salvar o item.')
      const mode = editor.mode
      setEditor(null)
      await loadData(false)
      window.dispatchEvent(new Event('menu:updated'))
      setFeedback({kind: 'success', message: mode === 'create' ? 'Item criado com sucesso.' : 'Alterações salvas.'})
    } catch (error: any) {
      setFeedback({kind: 'error', message: error?.message || 'Não foi possível salvar o item.'})
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteItem) return
    setDeleting(true)
    try {
      const response =
        deleteItem.type === 'category'
          ? await API.removeCategory(deleteItem.guid)
          : deleteItem.type === 'product'
          ? await API.removeProduct(deleteItem.guid)
          : await API.removeSubProduct(deleteItem.guid)
      if (!response?.success) throw new Error(response?.error || 'Não foi possível excluir o item.')
      setDeleteItem(null)
      await loadData(false)
      window.dispatchEvent(new Event('menu:updated'))
      setFeedback({kind: 'success', message: 'Item excluído do menu.'})
    } catch (error: any) {
      setFeedback({kind: 'error', message: error?.message || 'Não foi possível excluir o item.'})
    } finally {
      setDeleting(false)
    }
  }

  const renderRow = (
    item: MenuItem,
    parent: MenuItem | undefined,
    siblingIndex: number,
    siblingCount: number
  ) => {
    const Icon = getIcon(item.icon)
    const parentGuid = parent?.guid || null
    const hasChildren = item.children.length > 0
    const isExpanded = !!query.trim() || !!item.isOpen
    const meta =
      item.type === 'category'
        ? `${item.children.length} ${item.children.length === 1 ? 'produto' : 'produtos'}`
        : item.type === 'product'
        ? `${item.children.length} ${item.children.length === 1 ? 'subproduto' : 'subprodutos'}`
        : 'Destino final do menu'

    return (
      <div
        className={`gm-row gm-row--${item.type} ${dragging?.guid === item.guid ? 'gm-row--dragging' : ''} ${
          dragOverGuid === item.guid ? 'gm-row--drop-target' : ''
        }`}
        onDragOver={(event) => {
          if (
            dragging &&
            dragging.type === item.type &&
            dragging.parentGuid === parentGuid &&
            dragging.guid !== item.guid
          ) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
            setDragOverGuid(item.guid)
          }
        }}
        onDrop={(event) => {
          event.preventDefault()
          if (dragging) moveItem(dragging, item.guid)
          setDragging(null)
          setDragOverGuid(null)
        }}
      >
        {canEdit ? (
          <button
            type='button'
            className='gm-drag'
            draggable={!query.trim()}
            aria-label={`Arrastar ${item.name}`}
            title={query.trim() ? 'Limpe a busca para reordenar' : 'Arraste para reordenar'}
            onDragStart={(event) => {
              if (query.trim()) {
                event.preventDefault()
                return
              }
              event.dataTransfer.effectAllowed = 'move'
              event.dataTransfer.setData('text/plain', item.guid)
              setDragging({guid: item.guid, type: item.type, parentGuid})
            }}
            onDragEnd={() => {
              setDragging(null)
              setDragOverGuid(null)
            }}
          >
            <GripVertical size={18} />
          </button>
        ) : <span className='gm-spacer' />}
        {hasChildren ? (
          <button
            type='button'
            className='gm-expand'
            onClick={() => !query.trim() && toggleItem(item.guid)}
            aria-label={isExpanded ? `Recolher ${item.name}` : `Expandir ${item.name}`}
          >
            {isExpanded ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
          </button>
        ) : <span className='gm-spacer' />}
        <span className={`gm-item-icon gm-item-icon--${item.type}`}><Icon size={18} /></span>
        <div className='gm-item-copy'>
          <div className='gm-item-title' title={item.name}>{item.name}</div>
          <div className='gm-item-meta'>
            {item.originalName ? `${meta} · Nome administrativo: ${item.originalName}` : meta}
          </div>
        </div>
        {canEdit && (
          <div className='gm-actions'>
            {item.type !== 'subproduct' && (
              <button
                type='button'
                className='gm-add-button'
                onClick={() => setEditor({
                  mode: 'create',
                  type: item.type === 'category' ? 'product' : 'subproduct',
                  parent: item,
                })}
                title={`Adicionar ${item.type === 'category' ? 'produto' : 'subproduto'}`}
              >
                <Plus size={15} /><span>{item.type === 'category' ? 'Produto' : 'Subproduto'}</span>
              </button>
            )}
            <button
              type='button'
              className='gm-icon-button'
              onClick={() => moveByOffset(item, parentGuid, -1)}
              disabled={!!query.trim() || siblingIndex === 0}
              aria-label={`Mover ${item.name} para cima`}
              title='Mover para cima'
            >
              <ChevronDown size={16} style={{transform: 'rotate(180deg)'}} />
            </button>
            <button
              type='button'
              className='gm-icon-button'
              onClick={() => moveByOffset(item, parentGuid, 1)}
              disabled={!!query.trim() || siblingIndex === siblingCount - 1}
              aria-label={`Mover ${item.name} para baixo`}
              title='Mover para baixo'
            >
              <ChevronDown size={16} />
            </button>
            <button
              type='button'
              className='gm-icon-button'
              onClick={() => setEditor({mode: 'edit', type: item.type, item, parent})}
              aria-label={`Editar ${item.name}`}
              title='Editar nome e ícone'
            >
              <Edit3 size={15} />
            </button>
            <button
              type='button'
              className='gm-icon-button gm-icon-button--danger'
              onClick={() => setDeleteItem(item)}
              aria-label={`Excluir ${item.name}`}
              title='Excluir'
            >
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <MenuEditor>
      <ItemEditorModal state={editor} saving={saving} onClose={() => setEditor(null)} onSave={saveEditor} />
      <DeleteModal item={deleteItem} deleting={deleting} onClose={() => setDeleteItem(null)} onConfirm={confirmDelete} />

      <header className='gm-header'>
        <div>
          <div className='gm-kicker'>Administração</div>
          <h1>Estrutura do menu</h1>
          <p className='gm-subtitle'>
            Organize a navegação da plataforma em categorias, produtos e subprodutos. As mudanças de
            ordem são salvas assim que você move um item.
          </p>
        </div>
        {canEdit && (
          <button type='button' className='gm-primary' onClick={() => setEditor({mode: 'create', type: 'category'})}>
            <Plus size={17} /> Nova categoria
          </button>
        )}
      </header>

      <section className='gm-summary' aria-label='Resumo do menu'>
        <div className='gm-stat'><span className='gm-stat-icon'><Folder size={19} /></span><div><div className='gm-stat-value'>{totals.categories}</div><div className='gm-stat-label'>Categorias</div></div></div>
        <div className='gm-stat'><span className='gm-stat-icon'><Box size={19} /></span><div><div className='gm-stat-value'>{totals.products}</div><div className='gm-stat-label'>Produtos</div></div></div>
        <div className='gm-stat'><span className='gm-stat-icon'><FileBarChart2 size={19} /></span><div><div className='gm-stat-value'>{totals.subproducts}</div><div className='gm-stat-label'>Subprodutos</div></div></div>
      </section>

      <section className='gm-workspace'>
        <div className='gm-toolbar'>
          <div className='gm-search'>
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder='Buscar categoria, produto ou subproduto' aria-label='Buscar no menu' />
            {query && <button type='button' className='gm-search-clear' onClick={() => setQuery('')} aria-label='Limpar busca'><X size={15} /></button>}
          </div>
          <div className='gm-toolbar-actions'>
            <button type='button' className='gm-secondary' onClick={() => setAllOpen(true)}><PanelLeftOpen size={15} /> Expandir</button>
            <button type='button' className='gm-secondary' onClick={() => setAllOpen(false)}><PanelLeftClose size={15} /> Recolher</button>
          </div>
        </div>
        {canEdit && !query.trim() && (
          <div className='gm-help'><GripVertical size={15} /><span><strong>Reordenação rápida:</strong> arraste pelo marcador ou use as setas. Cada item mantém seu ícone e seus vínculos.</span></div>
        )}
        {feedback && (
          <div className={`gm-feedback gm-feedback--${feedback.kind}`} role='status'>
            {feedback.kind === 'success' ? <Check size={15} /> : <Bell size={15} />}{feedback.message}
          </div>
        )}
        <div className='gm-tree'>
          {loading ? (
            <div className='gm-loading'><Loader2 className='gm-spinner' size={24} /> Carregando estrutura do menu…</div>
          ) : visibleItems.length ? (
            visibleItems.map((category, categoryIndex) => (
              <article className='gm-category' key={category.guid}>
                {renderRow(category, undefined, categoryIndex, visibleItems.length)}
                {(query.trim() || category.isOpen) && category.children.map((product, productIndex) => (
                  <React.Fragment key={product.guid}>
                    {renderRow(product, category, productIndex, category.children.length)}
                    {(query.trim() || product.isOpen) && product.children.map((subproduct, subproductIndex) => (
                      <React.Fragment key={subproduct.guid}>
                        {renderRow(subproduct, product, subproductIndex, product.children.length)}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </article>
            ))
          ) : (
            <div className='gm-empty'>
              <Search size={24} />
              <strong>{query ? 'Nenhum item encontrado' : 'Seu menu está vazio'}</strong>
              <p>{query ? 'Tente buscar por outro nome ou limpe o campo de busca.' : 'Crie a primeira categoria para começar a estruturar a navegação.'}</p>
            </div>
          )}
        </div>
      </section>
    </MenuEditor>
  )
}

export default GeneralMenu
